import React, { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
    X,
    Copy,
    ExternalLink,
    Check,
    Loader2,
    QrCode,
    Sparkles,
    ShieldCheck,
    Heart,
} from "lucide-react";

const SOL_GRAD = "linear-gradient(95deg, #9945FF 0%, #DC1FFF 35%, #FF6FA1 60%, #14F195 100%)";

/**
 * Sovereign One-Click Crypto Checkout · USDC on Solana.
 * Mints an intent server-side → renders the Solana Pay QR + deep-link →
 * customer pays → click "I've sent it" → backend marks attested + writes
 * Foundry Ledger entry with 5% Sovereign Discount + 10% CyberKids tax.
 *
 * Receive-only. The application never holds the private key.
 */
export default function SolanaCheckout({
    open,
    onClose,
    amount,                  // gross amount in USDC (BEFORE discount)
    label = "EIGOO Sovereign Checkout",
    message = "Sovereign One-Click Checkout",
    sector,
    vessel,
    purpose,
    onAttested,
}) {
    const [phase, setPhase] = useState("preview");   // preview → minted → attested
    const [intent, setIntent] = useState(null);
    const [busy, setBusy] = useState(false);
    const [txSig, setTxSig] = useState("");
    const [copied, setCopied] = useState(false);

    const dismiss = () => {
        setPhase("preview");
        setIntent(null);
        setTxSig("");
        onClose?.();
    };

    const mintIntent = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/payments/solana/intent", {
                amount_usdc: amount,
                label,
                message,
                sector,
                vessel,
                purpose,
                apply_sovereign_discount: true,
            });
            setIntent(data);
            setPhase("minted");
        } catch {
            toast.error("Could not mint USDC intent. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const attest = async () => {
        if (!intent) return;
        setBusy(true);
        try {
            await api.post(`/payments/solana/intent/${intent.id}/confirm`, {
                tx_signature: txSig.trim() || null,
            });
            setPhase("attested");
            toast.success("Payment attested. Logged to Foundry Ledger.");
            onAttested?.(intent);
        } catch {
            toast.error("Could not attest payment.");
        } finally {
            setBusy(false);
        }
    };

    const copyAddress = async () => {
        try {
            await navigator.clipboard.writeText(intent?.recipient || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            toast.success("Treasury address copied.");
        } catch {
            toast.error("Copy failed.");
        }
    };

    if (!open) return null;

    const grossUsdc = intent ? intent.amount_gross_usdc : amount;
    const discount = intent ? intent.discount_amount_usdc : Number((amount * 0.05).toFixed(2));
    const charged = intent ? intent.amount_usdc : Number((amount - discount).toFixed(2));
    const cyberkids = intent ? intent.cyberkids_amount_usdc : Number((charged * 0.1).toFixed(2));

    return (
        <div
            data-testid="solana-checkout"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
            onClick={dismiss}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-lg rounded-3xl p-1"
                style={{ background: SOL_GRAD }}
            >
                <div className="rounded-[calc(1.5rem-4px)] bg-[hsl(0_0%_3%)] p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div
                                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full mb-3 text-[10px] uppercase tracking-[0.4em] font-mono"
                                style={{
                                    color: "#FF6FA1",
                                    border: "1px solid rgba(255,111,161,0.4)",
                                }}
                            >
                                <Sparkles size={11} /> Sovereign One-Click · USDC
                            </div>
                            <h3 className="font-heading text-2xl tracking-tight text-white leading-tight">
                                Pay with{" "}
                                <span
                                    className="bg-clip-text text-transparent"
                                    style={{ backgroundImage: SOL_GRAD }}
                                >
                                    USDC
                                </span>{" "}
                                on Solana
                            </h3>
                        </div>
                        <button
                            onClick={dismiss}
                            data-testid="solana-checkout-close"
                            className="text-white/55 hover:text-white p-2 -mr-2 -mt-2"
                            aria-label="Close checkout"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Sovereign Discount banner */}
                    <div
                        data-testid="sovereign-discount-banner"
                        className="mt-5 flex items-center gap-3 px-4 py-3 rounded-xl"
                        style={{
                            background: "rgba(20,241,149,0.06)",
                            border: "1px solid rgba(20,241,149,0.35)",
                        }}
                    >
                        <span
                            className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                            style={{
                                background: "rgba(20,241,149,0.12)",
                                border: "1px solid rgba(20,241,149,0.5)",
                            }}
                        >
                            <ShieldCheck size={15} style={{ color: "#14F195" }} />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div
                                className="text-[10px] uppercase tracking-[0.35em] font-mono"
                                style={{ color: "#14F195" }}
                            >
                                Sovereign Discount · 5% off
                            </div>
                            <div className="text-sm text-white/85 mt-0.5">
                                USDC payments save{" "}
                                <span className="font-mono font-bold text-[#14F195]">
                                    ${discount.toFixed(2)}
                                </span>{" "}
                                vs Stripe / PayPal.
                            </div>
                        </div>
                    </div>

                    {/* Price summary */}
                    <div className="mt-5 grid grid-cols-3 gap-3" data-testid="solana-checkout-summary">
                        <Cell label="Gross" value={`$${grossUsdc.toFixed(2)}`} dim />
                        <Cell
                            label="Discount"
                            value={`-$${discount.toFixed(2)}`}
                            color="#14F195"
                        />
                        <Cell
                            label="You pay"
                            value={`$${charged.toFixed(2)}`}
                            color="#FF6FA1"
                            big
                        />
                    </div>

                    {/* CyberKids tax disclosure */}
                    <div
                        data-testid="cyberkids-tax-line"
                        className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 font-mono"
                    >
                        <Heart size={10} style={{ color: "#FF6FA1" }} />
                        <span>
                            10% (${cyberkids.toFixed(2)}) auto-diverted to CyberKids Workstation Fund
                        </span>
                    </div>

                    {/* Phases */}
                    {phase === "preview" && (
                        <div className="mt-7">
                            <button
                                onClick={mintIntent}
                                disabled={busy}
                                data-testid="solana-checkout-mint"
                                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-black hover:scale-[1.01] transition-transform disabled:opacity-60"
                                style={{ background: SOL_GRAD }}
                            >
                                {busy ? (
                                    <>
                                        <Loader2 size={15} className="animate-spin" /> Minting intent...
                                    </>
                                ) : (
                                    <>
                                        <QrCode size={15} /> Generate Solana Pay
                                    </>
                                )}
                            </button>
                            <p
                                className="mt-4 text-xs text-white/55 text-center italic"
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                            >
                                "Voyaging through the stars, anchored in stable gold."
                            </p>
                        </div>
                    )}

                    {phase === "minted" && intent && (
                        <div className="mt-6">
                            {/* QR */}
                            {intent.qr_data_uri && (
                                <div className="flex flex-col items-center" data-testid="solana-qr">
                                    <div
                                        className="p-3 rounded-2xl bg-white"
                                        style={{
                                            boxShadow: "0 0 36px rgba(220,31,255,0.35)",
                                        }}
                                    >
                                        <img
                                            src={intent.qr_data_uri}
                                            alt="Solana Pay QR"
                                            width={220}
                                            height={220}
                                        />
                                    </div>
                                    <div className="mt-3 text-[10px] uppercase tracking-[0.4em] text-white/55 font-mono">
                                        Scan with Phantom / Solflare
                                    </div>
                                </div>
                            )}

                            {/* Deep-link button */}
                            <a
                                href={intent.pay_url}
                                data-testid="solana-deeplink"
                                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-black hover:scale-[1.01] transition-transform"
                                style={{ background: SOL_GRAD }}
                            >
                                Open in wallet <ExternalLink size={15} />
                            </a>

                            {/* Address row */}
                            <div className="mt-5 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                                <div className="text-[9px] uppercase tracking-[0.4em] text-white/55 font-mono mb-1">
                                    Treasury · receive-only
                                </div>
                                <div className="flex items-center gap-2">
                                    <code
                                        className="flex-1 text-[11px] font-mono text-white/85 break-all"
                                        data-testid="solana-treasury-addr"
                                    >
                                        {intent.recipient}
                                    </code>
                                    <button
                                        onClick={copyAddress}
                                        data-testid="solana-copy-addr"
                                        className="p-2 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                                    >
                                        {copied ? <Check size={13} /> : <Copy size={13} />}
                                    </button>
                                </div>
                                <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-white/45 font-mono">
                                    Memo · {intent.memo}
                                </div>
                            </div>

                            {/* Attestation */}
                            <div className="mt-5">
                                <label className="text-[10px] uppercase tracking-[0.35em] text-white/55 font-mono">
                                    Transaction signature (optional)
                                </label>
                                <input
                                    type="text"
                                    value={txSig}
                                    onChange={(e) => setTxSig(e.target.value)}
                                    data-testid="solana-tx-sig"
                                    placeholder="Paste tx signature from your wallet"
                                    className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-xs font-mono text-white focus:outline-none focus:border-[#FF6FA1]"
                                />
                                <button
                                    onClick={attest}
                                    disabled={busy}
                                    data-testid="solana-attest"
                                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold border border-[#14F195] text-[#14F195] hover:bg-[#14F195] hover:text-black transition-colors disabled:opacity-60"
                                >
                                    {busy ? (
                                        <>
                                            <Loader2 size={14} className="animate-spin" /> Attesting...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={14} /> I've sent it · Mark paid
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {phase === "attested" && (
                        <div className="mt-7 text-center">
                            <div
                                className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-4"
                                style={{
                                    background: "rgba(20,241,149,0.15)",
                                    border: "1px solid #14F195",
                                }}
                            >
                                <Check size={26} style={{ color: "#14F195" }} />
                            </div>
                            <div className="font-heading text-2xl text-white">
                                Logged to the Foundry Ledger
                            </div>
                            <p className="mt-3 text-sm text-white/65 italic">
                                "Voyaging through the stars, anchored in stable gold."
                            </p>
                            <button
                                onClick={dismiss}
                                data-testid="solana-checkout-done"
                                className="mt-6 px-6 py-2.5 rounded-full font-semibold text-black"
                                style={{ background: SOL_GRAD }}
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Cell({ label, value, color = "white", dim = false, big = false }) {
    return (
        <div
            className="rounded-xl p-3 bg-white/[0.025] border border-white/[0.08]"
            data-testid={`solana-cell-${label.toLowerCase()}`}
        >
            <div className="text-[9px] uppercase tracking-[0.35em] text-white/45 font-mono">
                {label}
            </div>
            <div
                className={`mt-1 font-mono ${big ? "text-2xl" : "text-base"}`}
                style={{ color: dim ? "rgba(255,255,255,0.55)" : color, opacity: dim ? 0.65 : 1 }}
            >
                {value}
            </div>
        </div>
    );
}
